import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, InputLabel, ListItemButton, ListItemText, Menu, MenuItem, OutlinedInput, Radio, RadioGroup, Select, SelectChangeEvent, Stack, TextField, Typography } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import FileUpload from '../presentation-components/miscellaneous-components/file-upload';
import FileUploadService from '../../shared/services/file-upload/file-upload.service';
import { ReportPeriodSelectComponent } from '../presentation-components/miscellaneous-components/report-period-select';
import { ReportPeriodContext } from '../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../context/user-notifications/user-notifications';
import { deleteQueryParamFromUrl } from '../../shared/utils/misc-shared-utils';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FileDownloadOutlined } from '@mui/icons-material';
import CustomFieldService from '../../shared/services/custom-field/custom-field.service';
import { ICustomField } from '../../shared/services/custom-field/custom-field.model';
import { useSearchParams } from 'react-router-dom';
import ContactSupplierService from '../../shared/services/contact-supplier/contact-supplier.service';
import { IContactSupplierDetails } from '../../shared/services/contact-supplier/contact-supplier.model';
import FileUploadSettingService from '../../shared/services/file-upload-setting/file-upload-setting.service';
import RuleService from '../../shared/services/rule/rule.service';
import { useIsReportPeriodLocked } from '../../hooks/use-is-report-period-locked';

export const FileUploaderDialog: React.FC<{
  dialogActive: boolean,
  cancelUpload: any,
  onSuccess: (response: any) => void,
  typeOfData: string,
  onFailure: (message?: string) => void
}> = ({
  dialogActive,
  cancelUpload,
  onSuccess,
  typeOfData,
  onFailure,
}) => {
    const [searchParams] = useSearchParams()
    //Services
    const userNotificationsContext = useContext(UserNotificationsContext);
    const reportPeriodContext = useContext(ReportPeriodContext)
    const customFieldService = new CustomFieldService()
    const fileUploadSettingService = new FileUploadSettingService()
    const fileUploadService = new FileUploadService()
    const contactSupplierService = ContactSupplierService.getInstance()
    const ruleService = new RuleService()
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    //State
    const [hasRules, setHasRules] = useState<boolean>(false)
    const [suppliers, setsuppliers] = useState<IContactSupplierDetails[]>()
    const [supplierId, setSupplierId] = useState<string>()
    const [fileData, setFileData] = useState<any>([]);
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [numberOfSheetsInFile, setNumberOfSheetsInFile] = useState<number>()
    const openDropDown = Boolean(anchorEl);
    //Loading State
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    //Use Effects
    useEffect(() => {
      customFieldService.getAllActive(`&entryType=transaction`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))

      contactSupplierService.getAllActiveSuppliers(false)
        .then(response => {
          setsuppliers(response)
        })
        .catch(err => console.log(err))

      ruleService.getAll()
        .then(response => {
          if (response.length > 0) {
            setHasRules(true)
          } else {
            formik.setFieldValue('emissionMappingMethod', 'manual')
          }
        })
        .catch(err => console.log(err))
    }, [])

    //Formik
    const distributionListSchema = Yup.object().shape({
      listName: Yup.string().required('List Name is required'),
    });

    const emissionSchema = Yup.object().shape({
      emissionEntryMethod: Yup.string().required('Type of emission file is required'),
      contactSupplierId: Yup.string().nullable(),
      emissionMappingMethod: Yup.string()
        .when('emissionEntryMethod', {
          is: 'calculate',
          then: schema =>
            schema
              .required('You must select an emission mapping mode'),
          otherwise: schema => schema.notRequired(),
        }),
      headingRow: Yup.number()
        .when('emissionEntryMethod', {
          is: 'calculate',
          then: schema =>
            schema
              .required('The row number of the headings in the file is required'),
          otherwise: schema => schema.notRequired(),
        }),
      sheetNumber: Yup.number()
        .when('emissionEntryMethod', {
          is: 'calculate',
          then: schema =>
            schema
              .required('The number of the sheet containing the data in the file is required'),
          otherwise: schema => schema.notRequired(),
        }),
    });

    const formik = useFormik({
      initialValues: {
        listName: '',
        emissionEntryMethod: '',
        contactSupplierId: null,
        headingRow: null,
        sheetNumber: null,
        emissionMappingMethod: 'manual',
      },
      validationSchema: typeOfData === 'distribution-list' ? distributionListSchema : typeOfData === 'emissions' ? emissionSchema : null,
      onSubmit: async (values) => {
        if (
          values.emissionEntryMethod === 'calculate' &&
          numberOfSheetsInFile &&
          values.sheetNumber &&
          values.sheetNumber > numberOfSheetsInFile
        ) {
          formik.setFieldError('sheetNumber', `The uploaded file only contains ${numberOfSheetsInFile} sheet${numberOfSheetsInFile === 1 ? '' : 's'}, please select a number between 1 and ${numberOfSheetsInFile}.`);
          return;
        }
        if (values.contactSupplierId === '') {
          values.contactSupplierId = null
        }
        if (values.headingRow === '') {
          values.headingRow = null
        }
        if (values.sheetNumber === '') {
          values.sheetNumber = null
        }
        if (!isUploadingFile) {
          completeUpload(values)
        }
      }
    })

    useEffect(() => {
      if (searchParams.has('supplierId')) {
        const supplierId = searchParams.get('supplierId')
        if (supplierId) {
          setSupplierId(supplierId)
          formik.setFieldValue('emissionEntryMethod', 'calculate')
          formik.setFieldValue('contactSupplierId', supplierId)
        }
        deleteQueryParamFromUrl(searchParams, 'supplierId')
      }
    }, [searchParams])

    useEffect(() => {
      if (fileData.length > 0) {
        if (supplierId) {
          fileUploadSettingService.getAll(`?contactSupplierId=${supplierId}&fileType=emissions&mimetype=${fileData[0].type}`)
            .then(response => {
              if (response.length > 0) {
                formik.setFieldValue('headingRow', response[0].headingRow)
                formik.setFieldValue('emissionMappingMethod', response[0].mappingMethod)
              }
              if (fileData[0].type === 'text/csv') {
                formik.setFieldValue('sheetNumber', 1)
                formik.setFieldValue('headingRow', 1)
              } else if (numberOfSheetsInFile === 1) {
                formik.setFieldValue('sheetNumber', 1)
              } else if (response.length > 0) {
                formik.setFieldValue('sheetNumber', response[0].sheetNumber)
              }
            })
            .catch(err => console.log(err))
        }
        if (!supplierId) {
          if (fileData[0].type === 'text/csv') {
            formik.setFieldValue('sheetNumber', 1)
            formik.setFieldValue('headingRow', 1)
          } else if (typeOfData === 'emissions' && formik.values.emissionEntryMethod === 'calculate') {
            formik.setFieldValue('headingRow', 1)
            if (numberOfSheetsInFile === 1) {
              formik.setFieldValue('sheetNumber', 1)
            } else {
              formik.setFieldValue('sheetNumber', 2)
            }
          }
        }
      }
    }, [fileData, supplierId, numberOfSheetsInFile, formik.values.emissionEntryMethod])

    const completeUpload = (values?: any) => {
      setIsUploadingFile(true)
      const form = new FormData();
      for (let i = 0; i < fileData.length; i++) {
        form.append("files", fileData[i]);
      }
      form.append('details', JSON.stringify(typeOfData));
      if (typeOfData === 'distribution-list') {
        form.append('distributionListName', JSON.stringify(values.listName))
      } else if (typeOfData === 'emissions') {
        form.append('emissionEntryMethod', JSON.stringify(values.emissionEntryMethod))
        if (values.emissionEntryMethod === 'calculate') {
          form.append('emissionMappingMethod', JSON.stringify(values.emissionMappingMethod))
          form.append('contactSupplierId', JSON.stringify(values.contactSupplierId))
          form.append('sheetNumber', JSON.stringify(values.sheetNumber))
          form.append('headingRow', JSON.stringify(values.headingRow))
        }
      }
      if (isReportPeriodLocked === true && (typeOfData === 'emissions' || typeOfData === 'spend-based')) {
        userNotificationsContext.openDialog({ active: true, title: 'Action Not Allowed', message: `The selected report period is locked and cannot be modified`, buttonCopy: 'Ok', hideCancelButton: true })
      } else {
        if (fileData[0].type === 'text/csv') {
          const reader = new FileReader()
          reader.onloadend = () => {
            if (reader.result) {
              fileUploadService.uploadFile(form, `?reportPeriodId=${reportPeriodContext.lastSelectedReportId}`)
                .then(response => {
                  setFileData([]);
                  onSuccess(response)
                  formik.resetForm()
                  setIsUploadingFile(false)
                })
                .catch(err => {
                  if (typeOfData === 'locations' || typeOfData === 'distribution-list') {
                    if (err.response.data.code === 2) {
                      onFailure(err.response.data.message)
                    }
                    if (err.response.data.code === 1) {
                      const message = err.response.data.detailedMessage.map((m: any) => {
                        const numberedErrors = m.errors.map((error: string, i: number) => `${i + 1}) ${error}`).join(' ')
                        return `Row ${m.index} - ${numberedErrors}`
                      })
                      onFailure(message)
                    }
                  } else {
                    onFailure()
                  }
                  userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  setFileData([]);
                  formik.resetForm()
                  setIsUploadingFile(false)
                })
            }
          }
          reader.readAsArrayBuffer(fileData[0])
        } else {
          fileUploadService.uploadFile(form, `?reportPeriodId=${reportPeriodContext.lastSelectedReportId}`)
            .then(response => {
              setFileData([]);
              onSuccess(response)
              setIsUploadingFile(false)
            })
            .catch(err => {
              if (typeOfData === 'locations' || typeOfData === 'distribution-list') {
                if (err.response.data.code === 1) {
                  const message = err.response.data.detailedMessage.map((m: any) => {
                    const numberedErrors = m.errors.map((error: string, i: number) => `${i + 1}) ${error}`).join(' ')
                    return `Row ${m.index} - ${numberedErrors}`
                  })
                  onFailure(message)
                } else if (err.response.data.code === 2) {
                  onFailure(err.response.data.message)
                } else {
                  onFailure()
                }
              } else {
                onFailure()
              }
              setFileData([]);
              setIsUploadingFile(false)
            })
        }
      }
    }

    // const downloadCsvHandler = (emissionEntryMethod?: string) => {
    //   if (typeOfData === 'emissions' && emissionEntryMethod === 'calculate') {
    //     const baseHeaders = [
    //       'Custom Rule',
    //       'Group',
    //       'Category',
    //       'Level 2',
    //       'Level 3',
    //       'Level 4',
    //       'Level 5',
    //       'Unit of Measurement',
    //       'Units Consumed',
    //       'Start Date',
    //       'End Date',
    //       'Subsidiary',
    //       'Location',
    //       'Project',
    //       'Supplier',
    //     ]
    //     const customHeaders = customFields?.map(field => field.fieldName)
    //     const fullHeaders = [...baseHeaders, ...customHeaders ?? []];
    //     downloadFile({
    //       data: [...fullHeaders].join(','),
    //       fileName: 'Activity-Based Emissions.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'emissions' && emissionEntryMethod === 'direct') {
    //     const baseHeaders = [
    //       'Group',
    //       'Category',
    //       'Units Consumed (kgCO2e)',
    //       'Data Type',
    //       'Start Date',
    //       'End Date',
    //       'Subsidiary',
    //       'Location',
    //       'Project',
    //       'Supplier',
    //       // 'Vehicle',
    //       // 'Part of Regulated Emission Trading Scheme',
    //       // 'Regulated Emission Trading Scheme',
    //       'Additional Information 1',
    //       'Additional Information 2',
    //     ];
    //     const customHeaders = customFields?.map(field => field.fieldName)
    //     const fullHeaders = [...baseHeaders, ...customHeaders ?? []];
    //     downloadFile({
    //       data: [...fullHeaders].join(','),
    //       fileName: 'Direct Entry Emissions.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'emissions' && emissionEntryMethod === 'good') {
    //     const baseHeaders = [
    //       'Name of Good',
    //       'Number of Units Purchased',
    //       'Purchased Date',
    //       'Subsidiary',
    //       'Location',
    //       'Project',
    //     ];
    //     const customHeaders = customFields?.map(field => field.fieldName)
    //     const fullHeaders = [...baseHeaders, ...customHeaders ?? []];
    //     downloadFile({
    //       data: [...fullHeaders].join(','),
    //       fileName: 'Goods-Based Emissions.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'locations') {
    //     let headers = ['Name,Location Reference,Primary Location,Building Type,Ownership,Control Status,Floor Space,Number of Employees,Start Date,Address Line 1,Address Line 2,City,Postcode,Country,Electricity,Electricity Billing Period,Gas,Gas Billing Period,AirCon,AirCon Billing Period,Heat and Steam,Heat and Steam Billing Period'];
    //     downloadFile({
    //       data: [...headers].join('\n'),
    //       fileName: 'Locations.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'vehicles') {
    //     let headers = ['Vehicle Identification Number,Vehicle Registration,Vehicle Reference,Ownership,Frequency of Data Entry,Vehicle Type,Start Date,Default Subsidiary,Assigned User'];
    //     downloadFile({
    //       data: [...headers].join('\n'),
    //       fileName: 'Vehicle.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'spend-based') {
    //     const baseHeaders = [
    //       'Invoice Number',
    //       'Bank Account Name',
    //       'Total (£)',
    //       'Reference',
    //       'Date',
    //       'Category',
    //       'Supplier (Select Option)',
    //       'Supplier (Free Text Option)',
    //       'Subsidiary',
    //       'Location',
    //       'Project',
    //     ]
    //     const customHeaders = customFields?.map(field => field.fieldName)
    //     const fullHeaders = [...baseHeaders, ...customHeaders ?? []];
    //     downloadFile({
    //       data: [...fullHeaders].join(','),
    //       fileName: 'Spend-Based Emissions.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'distribution-list') {
    //     let headers = ['First Name,Surname,Email Address'];
    //     downloadFile({
    //       data: [...headers].join('\n'),
    //       fileName: 'Distribution List.csv',
    //       fileType: 'text/csv',
    //     })
    //   } else if (typeOfData === 'goods') {
    //     let headers = ['Type of Good,Name,Description,Model Number,Category,Subcategory One,Subcategory Two,Unit Type,Unit Amount,Emissions (kgCO2e per Unit),Supplier (Free Text Option)'];
    //     downloadFile({
    //       data: [...headers].join('\n'),
    //       fileName: 'Goods.csv',
    //       fileType: 'text/csv',
    //     })
    //   }
    // }

    const getExcelTemplate = (emissionEntryMethod?: string) => {
      const typeOfExcel = typeOfData === 'emissions' && emissionEntryMethod === 'product' ? 'emissions-product' :
        typeOfData === 'emissions' && emissionEntryMethod === 'direct' ? 'emissions-direct' :
          typeOfData
      fileUploadService.getExcelTemplate(typeOfExcel)
        .then(response => {
          if (typeOfData === 'emissions' && emissionEntryMethod === 'calculate') {
            createExcel(response, 'Activity-Based Emissions')
          } else if (typeOfData === 'emissions' && emissionEntryMethod === 'direct') {
            createExcel(response, 'Direct Entry Emissions')
          } else if (typeOfData === 'emissions' && emissionEntryMethod === 'product') {
            createExcel(response, 'Product-Based Emissions')
          } else if (typeOfData === 'locations') {
            createExcel(response, 'Locations')
          } else if (typeOfData === 'vehicles') {
            createExcel(response, 'Vehicles')
          } else if (typeOfData === 'spend-based') {
            createExcel(response, 'Spend-Based Emissions')
          } else if (typeOfData === 'distribution-list') {
            createExcel(response, 'Distribution List')
          } else if (typeOfData === 'products') {
            createExcel(response, 'Products')
          }
        })
    }

    const createExcel = (response: any, fileName: string) => {
      let filename = `${fileName}.xlsx`
      const url = window.URL.createObjectURL(new Blob([response.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    }

    const storeData = async (file: any) => {
      setFileData([...fileData, file]);
      if (typeOfData === 'emissions' && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        const form = new FormData();
        form.append("files", file);
        fileUploadService.getNumberOfSheetsInExcelFile(form)
          .then((response: any) => {
            setNumberOfSheetsInFile(response.numberOfSheets)
          })
          .catch(err => console.log(err))
      }
    };

    const handleCancelUpload = () => {
      setFileData([]);
      cancelUpload();
      formik.resetForm()
      setIsUploadingFile(false)
    }

    return (
      <Dialog open={dialogActive} maxWidth='sm' fullWidth>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', direction: 'row', alignItems: 'center', justifyContent: 'space-between', width: '100%' }} >
            {typeOfData !== 'distribution-list' && <DialogTitle sx={{ p: 0 }}>Add a Resource</DialogTitle>}
            {typeOfData === 'distribution-list' && <DialogTitle sx={{ p: 0 }}> Create Distribution List</DialogTitle>}
            {!supplierId && typeOfData !== 'emissions' &&
              <Button onClick={(e) => { getExcelTemplate() }}>
                <FileDownloadOutlined />
                <Typography>Download Template (.xlsx)</Typography>
              </Button>
            }
            {!supplierId && typeOfData === 'emissions' &&
              <Button onClick={(e) => { setAnchorEl(e.currentTarget) }}>
                <FileDownloadOutlined />
                <Typography>Download Template</Typography>
              </Button>}
            <Menu anchorEl={anchorEl} open={openDropDown} onClose={(e) => { setAnchorEl(null) }} elevation={3}>
              {/* {typeOfData !== 'emissions' && <>
                <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => downloadCsvHandler()} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> CSV </ListItemText>
                  </ListItemButton>
                </MenuItem>
                <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => getExcelTemplate()} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Excel </ListItemText>
                  </ListItemButton>
                </MenuItem>
              </>} */}
              {typeOfData === 'emissions' && <>
                {/* <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => downloadCsvHandler('calculate')} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Activity-Based Emissions CSV </ListItemText>
                  </ListItemButton>
                </MenuItem> */}
                <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => getExcelTemplate('calculate')} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Activity-Based Emissions (.xlsx) </ListItemText>
                  </ListItemButton>
                </MenuItem>
                {/* <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => downloadCsvHandler('direct')} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Direct Input Scope 3 Emissions CSV </ListItemText>
                  </ListItemButton>
                </MenuItem> */}
                <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => getExcelTemplate('direct')} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Direct Entry Emissions (.xlsx) </ListItemText>
                  </ListItemButton>
                </MenuItem>
                {/* <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => downloadCsvHandler('good')} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Goods-Based Emissions CSV </ListItemText>
                  </ListItemButton>
                </MenuItem> */}
                <MenuItem onClick={(e) => { setAnchorEl(null) }} sx={{ width: '100%', height: '35px' }}>
                  <ListItemButton onClick={() => getExcelTemplate('product')} disableRipple sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
                    <ListItemText> Product-Based Emissions (.xlsx) </ListItemText>
                  </ListItemButton>
                </MenuItem>
              </>}
            </Menu>
          </Box>
          <DialogContent sx={{ pt: 2, pl: 0, pr: 0 }}>
            {supplierId && suppliers && <FormControl sx={{ mb: 2, mt: 2 }} fullWidth>
              <InputLabel htmlFor='contactSupplierId'>Supplier</InputLabel>
              <Select
                id='contactSupplierId'
                name='contactSupplierId'
                label='Supplier'
                disabled={!!supplierId}
                value={formik.values.contactSupplierId || ''}
                error={formik.touched.contactSupplierId && Boolean(formik.errors.contactSupplierId)}
              // onChange={formik.handleChange}
              >
                <MenuItem value={supplierId}>{suppliers.find(s => s.id === supplierId)?.name}</MenuItem>
              </Select>
              {formik.touched.contactSupplierId && formik.errors.contactSupplierId && (
                <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>{formik.errors.contactSupplierId}</FormHelperText>
              )}
            </FormControl>}
            {typeOfData === 'emissions' &&
              <>
                <FormControl sx={{ mb: 2 }} fullWidth>
                  <InputLabel htmlFor='emissionEntryMethod'>Emission Type</InputLabel>
                  <Select
                    id='emissionEntryMethod'
                    name='emissionEntryMethod'
                    label='emissionType'
                    disabled={!!supplierId}
                    value={formik.values.emissionEntryMethod || ''}
                    error={formik.touched.emissionEntryMethod && Boolean(formik.errors.emissionEntryMethod)}
                    onChange={formik.handleChange}>
                    <MenuItem value='calculate'>Activity-Based Emissions</MenuItem>
                    <MenuItem value='direct'>Direct Entry Emissions</MenuItem>
                    <MenuItem value='product'>Product-Based Emissions</MenuItem>
                  </Select>
                  {formik.touched.emissionEntryMethod && formik.errors.emissionEntryMethod && (
                    <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>{formik.errors.emissionEntryMethod}</FormHelperText>
                  )}
                </FormControl>
              </>}
            {(typeOfData === 'emissions' || typeOfData === 'spend-based') &&
              <ReportPeriodSelectComponent onlyOpenReports />
            }
            {typeOfData === 'distribution-list' &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2, mb: 2 }}>
                <InputLabel htmlFor='listName'>Distribution List Name</InputLabel>
                <OutlinedInput
                  id='listName'
                  name='listName'
                  type='text'
                  label='Distribution List Name'
                  value={formik.values.listName}
                  onChange={formik.handleChange}
                  error={formik.touched.listName && Boolean(formik.errors.listName)}
                />
                {formik.touched.listName && formik.errors.listName && (
                  <FormHelperText sx={{ color: "#bf3333", ml: 2 }}>{formik.errors.listName}</FormHelperText>
                )}
              </FormControl>
            }
            <Box sx={{ mt: 2 }}>
              <FileUpload
                csv={true}
                excel={true}
                allowedFileTypes={['CSV', 'XLSX']}
                handleFileUpload={storeData}
              />
            </Box>
            {fileData && fileData.length > 0 && typeOfData === 'emissions' && formik.values.emissionEntryMethod === 'calculate' &&
              <Stack sx={{ mt: 2 }}>
                <Typography variant='subtitle1' sx={{ mb: 1 }}>Emission Mapping Mode</Typography>
                <FormControl sx={{ ml: 1 }}>
                  <RadioGroup
                    onChange={formik.handleChange}
                    value={formik.values.emissionMappingMethod}
                    id={'emissionMappingMethod'} name={'emissionMappingMethod'}>
                    <FormControlLabel value={'manual'} control={<Radio disabled={!hasRules} />} label={'Manual Hierarchy (Group > Category > Level 2-5)'}
                    />
                    <FormControlLabel value={'rule'} control={<Radio disabled={!hasRules} />} label={'Custom Rule'}
                    />
                  </RadioGroup>
                </FormControl>
              </Stack>
            }
            {fileData && fileData.length > 0 && fileData[0].type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && typeOfData === 'emissions' && formik.values.emissionEntryMethod === 'calculate' &&
              <>
                {numberOfSheetsInFile && numberOfSheetsInFile > 1 && <TextField
                  sx={{ mt: 2 }}
                  fullWidth
                  variant='outlined'
                  id='sheetNumber'
                  name='sheetNumber'
                  label='Sheet Number'
                  type='number'
                  inputProps={{ min: 1, max: numberOfSheetsInFile }}
                  value={formik.values.sheetNumber !== null && formik.values.sheetNumber !== undefined
                    ? formik.values.sheetNumber
                    : ''}
                  onChange={formik.handleChange}
                  disabled={formik.values.emissionEntryMethod !== 'calculate'}
                  error={formik.touched.sheetNumber && Boolean(formik.errors.sheetNumber)}
                  helperText={formik.touched.sheetNumber && formik.errors.sheetNumber}
                />}
                <TextField
                  sx={{ mt: 2 }}
                  fullWidth
                  variant='outlined'
                  id='headingRow'
                  name='headingRow'
                  label='Row Number of Headings'
                  type='number'
                  inputProps={{ min: 1 }}
                  value={formik.values.headingRow !== null && formik.values.headingRow !== undefined
                    ? formik.values.headingRow
                    : ''}
                  onChange={formik.handleChange}
                  disabled={formik.values.emissionEntryMethod !== 'calculate'}
                  error={formik.touched.headingRow && Boolean(formik.errors.headingRow)}
                  helperText={formik.touched.headingRow && formik.errors.headingRow}
                />
              </>}
          </DialogContent>
          <DialogActions>
            <Stack direction='row' spacing={1} justifyContent={'flex-end'} alignItems='center' sx={{ width: '100%' }}>
              <Stack direction='row' spacing={1} >
                <Button variant='text' onClick={() => handleCancelUpload()} disabled={isUploadingFile}><Typography sx={{ p: 0.5 }}>Cancel</Typography></Button>
                <Button
                  disabled={fileData.length === 0 || isUploadingFile || isReportPeriodLocked === true}
                  onClick={formik.submitForm}
                  sx={{ minWidth: '100px' }}>
                  <Typography>
                    {isUploadingFile ? <CircularProgress size={14} /> : 'Upload'}
                  </Typography>
                </Button>
              </Stack>
            </Stack>
          </DialogActions>
        </Box>
      </Dialog >
    )
  }